import React, { useState, useEffect } from 'react';
import { Ship, Anchor, Hotel, Utensils, Sparkles, Heart, Download, Phone, Mail, MapPin, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { features, testimonials, inclusions } from '../data/mock';
import InquiryForm from '../components/InquiryForm';
import SuitesSection from '../components/SuitesSection';
import './ExploraLanding.css';

const iconMap = {
  ship: Ship,
  anchor: Anchor,
  hotel: Hotel,
  utensils: Utensils,
  sparkles: Sparkles,
  heart: Heart
};

// Simplified destination data - SEO optimized
const destinations = [
  {
    id: 'alaska',
    name: 'Alaska',
    title: 'Explora Journeys Alaska Cruises',
    subtitle: '7-10 Night Luxury Alaska Glacier Cruises',
    description: 'Experience breathtaking Alaska glacier cruises aboard EXPLORA II. Sail through pristine fjords, witness calving glaciers, and explore the untamed wilderness of the Last Frontier on an all-inclusive luxury cruise.',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_3fccb5b2-fa22-46bc-b877-1894d1f94e74/artifacts/7eqn6qqw_ChatGPT%20Image%20Jul%2027%2C%202026%2C%2002_21_32%20PM.jpg',
    priceFrom: 'USD 4,500',
    link: 'https://www.clickmycruise.com/cruises/results.aspx?accion=searchcruises&searchSlot=2f95b4c0-f08e-40dc-9e2e-9de81b2a0bcf&rand=639207491846665972&destinations=Alaska&searching=true&vendors=EXL&searchcurrency=USD'
  },
  {
    id: 'caribbean',
    name: 'Caribbean',
    title: 'Explora Journeys Caribbean Cruises',
    subtitle: '7-14 Night Luxury Caribbean Island Cruises',
    description: 'Discover paradise on a luxury Caribbean cruise aboard EXPLORA I and EXPLORA II. Visit pristine beaches, turquoise waters, and vibrant island cultures on an all-suite, all-inclusive voyage.',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_3fccb5b2-fa22-46bc-b877-1894d1f94e74/artifacts/5xwvbc4c_Caribbean.jpg',
    priceFrom: 'USD 3,800',
    link: 'https://www.clickmycruise.com/cruises/results.aspx?accion=searchcruises&searchSlot=2f95b4c0-f08e-40dc-9e2e-9de81b2a0bcf&rand=639207491846665972&destinations=CARIBBEAN&searching=true&vendors=EXL&searchcurrency=USD'
  },
  {
    id: 'mediterranean',
    name: 'Mediterranean',
    title: 'Explora Journeys Mediterranean Cruises',
    subtitle: '7-12 Night Luxury Mediterranean Cruises',
    description: 'Sail the Mediterranean in ultimate luxury aboard EXPLORA I, EXPLORA II, and EXPLORA III. Explore Greek islands, Italian coastlines, and French Riviera ports on an elegant, yacht-inspired ocean voyage.',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_3fccb5b2-fa22-46bc-b877-1894d1f94e74/artifacts/5c0ntn75_EXPLORA%20II%20-%20Mediterranean%203.jpg',
    priceFrom: 'USD 4,200',
    link: 'https://www.clickmycruise.com/cruises/results.aspx?accion=searchcruises&searchSlot=2f95b4c0-f08e-40dc-9e2e-9de81b2a0bcf&rand=639207491846665972&destinations=MEDITERRANEAN&searching=true&vendors=EXL&searchcurrency=USD'
  }
];

const ExploraLanding = () => {
  const [showInquiryForm, setShowInquiryForm] = useState(false);

  // Send height updates to parent iframe
  useEffect(() => {
    const sendHeightToParent = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'exploraHeight', height }, '*');
    };

    // Send initial height
    sendHeightToParent();

    // Send height on window resize
    window.addEventListener('resize', sendHeightToParent);

    // Send height on content changes (use MutationObserver)
    const observer = new MutationObserver(sendHeightToParent);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Send height periodically (for dynamic content)
    const interval = setInterval(sendHeightToParent, 500);

    return () => {
      window.removeEventListener('resize', sendHeightToParent);
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Also send height when modals open/close
  useEffect(() => {
    const height = document.documentElement.scrollHeight;
    window.parent.postMessage({ type: 'exploraHeight', height }, '*');
  }, [showInquiryForm]);

  const handleDownloadBrochure = () => {
    window.open('https://customer-assets.emergentagent.com/job_ocean-escapes/artifacts/3n1p7qzb_Explora%20Journeys%20Brochure.pdf', '_blank');
  };

  const scrollToItineraries = () => {
    const itinerariesSection = document.querySelector('.itineraries-section');
    if (itinerariesSection) {
      itinerariesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="explora-landing">
      {/* Intro Section */}
      <section className="explora-intro-section">
        <div className="container">
          <h1 className="explora-intro-title">Explora Journeys India</h1>
          <p className="explora-intro-text">
            ClickMyCruise is the sole representative and General Sales Agent (GSA) for Explora Journeys in India, offering all-suite, all-inclusive 
            luxury ocean cruises to Europe, the Mediterranean, the Middle East and the Monaco F1 Grand Prix. Book with confidence and a best rate guarantee.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section hero-explora-ship">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="explora-logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_ocean-escapes/artifacts/nw12g7lm_Explora%20White%20Logo.png" 
              alt="Explora Journeys" 
              className="explora-logo"
            />
          </div>
          <div className="exclusive-badge">
            ClickMyCruise - Exclusive Representative in India
          </div>
          <h1 className="hero-title">
            Experience Unparalleled Luxury at Sea
          </h1>
          <p className="hero-subtitle">
            All-suite, all-inclusive luxury cruising. Explore the world's most spectacular destinations aboard elegant, yacht-inspired ships.
          </p>
          <div className="hero-buttons">
            <Button size="lg" className="cta-primary" onClick={scrollToItineraries}>
              View Journeys
            </Button>
            <Button size="lg" variant="outline" className="cta-secondary" onClick={handleDownloadBrochure}>
              <Download className="mr-2 h-5 w-5" />
              Download Brochure
            </Button>
          </div>
        </div>
      </section>

      {/* Ocean State of Mind Section */}
      <section className="ocean-state-section">
        <div className="container">
          <div className="ocean-state-content">
            <div className="ocean-state-text">
              <h2 className="ocean-state-title">Discover the Ocean State of Mind</h2>
              <p className="ocean-state-description">
                Sail on thoughtfully curated ocean journeys of discovery to both iconic and hidden destinations.
              </p>
              <p className="ocean-state-description">
                Aboard our elegant, super-yacht-inspired ships, every detail is considered, allowing time itself to become the ultimate luxury.
              </p>
            </div>
            <div className="ocean-state-images">
              <div className="ocean-image-primary">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ocean-escapes/artifacts/ecejw2vu_Astern-1.webp" 
                  alt="Ocean Deck"
                  className="ocean-img"
                  loading="lazy"
                />
              </div>
              <div className="ocean-image-secondary">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ocean-escapes/artifacts/zrh6ymi2_zakhynthos.jpg" 
                  alt="Greek Islands"
                  className="ocean-img"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title text-center">Why Choose Explora Journeys</h2>
          <div className="features-grid">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={index} className="feature-card">
                  <CardContent className="feature-content">
                    <div className="feature-icon">
                      <IconComponent className="icon" />
                    </div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brand Ambassador */}
      <section className="ambassador-section">
        <div className="container">
          <div className="ambassador-grid">
            <div className="ambassador-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_ocean-escapes/artifacts/mlfvb3e3_jannik%20sinner.jpeg" 
                alt="Jannik Sinner"
                className="featured-image"
                loading="lazy"
              />
            </div>
            <div className="ambassador-content">
              <div className="ambassador-badge">BRAND AMBASSADOR</div>
              <h2 className="section-title">Jannik Sinner</h2>
              <p className="ambassador-text">
                World-renowned tennis champion Jannik Sinner embodies the Explora Journeys philosophy of precision, excellence, and mindful living. His partnership reflects our shared values of wellness, balance, and the pursuit of perfection.
              </p>
              <p className="ambassador-text">
                Just as Jannik finds strength in recovery and connection with nature, Explora Journeys offers a sanctuary where the ocean nurtures body and soul.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="itineraries-section" id="explora-cruises">
        <div className="container">
          <h2 className="section-title text-center">Explora Journeys Luxury Cruise Destinations</h2>
          <p className="section-subtitle text-center">
            All-Suite, All-Inclusive Luxury Ocean Cruises aboard EXPLORA I, EXPLORA II & EXPLORA III
          </p>

          <div className="destinations-grid">
            {destinations.map((destination) => (
              <Card key={destination.id} className="destination-card" itemScope itemType="https://schema.org/Product">
                <div className="destination-image-container">
                  <img src={destination.image} alt={`${destination.title} - Luxury Cruise`} className="destination-image" loading="lazy" itemProp="image" />
                  <div className="destination-overlay">
                    <span className="destination-badge">{destination.name}</span>
                  </div>
                </div>
                <CardContent className="destination-content">
                  <h3 className="destination-title" itemProp="name">{destination.title}</h3>
                  <p className="destination-subtitle">{destination.subtitle}</p>
                  <p className="destination-description" itemProp="description">{destination.description}</p>
                  <div className="destination-footer">
                    <div className="destination-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                      <span className="price-label">From</span>
                      <span className="price-value" itemProp="price">{destination.priceFrom}</span>
                      <span className="price-label">per guest</span>
                      <meta itemProp="priceCurrency" content="USD" />
                    </div>
                    <Button 
                      className="destination-btn"
                      onClick={() => window.open(destination.link, '_blank')}
                      aria-label={`View ${destination.name} luxury cruise itineraries`}
                    >
                      Explore Journeys
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Suites Section */}
      <SuitesSection />

      {/* Inclusions Section */}
      <section className="inclusions-section">
        <div className="container">
          <h2 className="section-title text-center">All Journeys Include</h2>
          <div className="inclusions-grid">
            {inclusions.map((item, index) => (
              <div key={index} className="inclusion-item">
                <ChevronRight className="inclusion-icon" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <div className="testimonials-slider">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <p className="testimonial-author">— {testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2 className="section-title">Let's Plan Your Journey</h2>
              <p className="contact-description">
                As the exclusive representative of Explora Journeys in India, ClickMyCruise is here to help you discover the ocean state of mind.
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <div>
                    <p className="contact-label">Call Us</p>
                    <p className="contact-value">+91 9820110644</p>
                  </div>
                </div>
                <div className="contact-item">
                  <Mail className="contact-icon" />
                  <div>
                    <p className="contact-label">Email Us</p>
                    <p className="contact-value">info@clickmycruise.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <MapPin className="contact-icon" />
                  <div>
                    <p className="contact-label">Visit Us</p>
                    <p className="contact-value">154A Mittal Court, Nariman Point, Mumbai 400021</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form-container">
              <InquiryForm />
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form Modal */}
      {showInquiryForm && (
        <div className="modal-overlay" onClick={() => setShowInquiryForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowInquiryForm(false)}>
              ×
            </button>
            <h2 className="modal-title">Request Your Journey</h2>
            <InquiryForm 
              onSuccess={() => setShowInquiryForm(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploraLanding;
